import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StateChip, Missing, ForSaleAs } from "@/components/sales/SalesRow";
import { UndoBar, useUndoBar } from "@/components/sales/UndoBar";
import { DraftField } from "@/components/sales/draft/DraftField";
import { DraftPhotos } from "@/components/sales/draft/DraftPhotos";
import { DraftFirewall } from "@/components/sales/draft/DraftFirewall";
import { DraftReview, DraftBlocks } from "@/components/sales/draft/DraftReview";
import { DraftPreview } from "@/components/sales/draft/DraftPreview";
import { DraftSeo } from "@/components/sales/draft/DraftSeo";
import { draftAct } from "@/components/sales/draft/draftApi";
import { ArrowLeft, Loader2 } from "lucide-react";

// THE LISTING DRAFT (S2.3). The record on the PRIVATE side, the listing on the PUBLIC side — side by side on a PC,
// stacked on a tablet or phone. Editing the listing never changes the part or truck itself (D103).
export function DraftView({ kind, id, onClose }) {
  const [v, setV] = useState(null);
  const bar = useUndoBar();
  const load = useCallback(() => api.get(`/sales/draft/${kind}/${id}`).then((r) => setV(r.data))
    .catch((e) => setV({ error: formatApiErrorDetail(e.response?.data?.detail) })), [kind, id]);
  useEffect(() => { load(); }, [load]);
  const onView = (data) => { bar.push(data); if (data?.view) setV(data.view); };
  if (!v) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;
  return (
    <div data-testid="sales-draft" className="space-y-4">
      <button type="button" data-testid="sales-draft-close" onClick={onClose}
        className="inline-flex items-center gap-1.5 h-11 px-3 rounded-lg border-2 border-slate-300 bg-white text-sm font-bold hover:bg-slate-50"><ArrowLeft className="w-4 h-4" />Back to the queue</button>
      {v.error ? <p data-testid="sales-draft-error" className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm">{v.error}</p> : <Body v={v} onView={onView} />}
      <UndoBar bar={bar} after={load} testid="sales-draft-undo-bar" />
    </div>
  );
}

function Body({ v, onView }) {
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 data-testid="sales-draft-title" className="font-display text-xl font-bold text-slate-900">Listing draft — {v.title}</h2>
          <div className="font-mono text-xs text-slate-500">{v.kind === "truck" ? "Truck" : "Part"}{v.code ? ` · ${v.code}` : ""}</div>
        </div>
        <StateChip state={v.state} words={v.state_words} testid="sales-draft-state" />
      </div>
      {v.for_sale_as && <ForSaleAs a={v.for_sale_as} testid="sales-draft-as" />}
      <Missing missing={v.complete ? [] : v.missing} testid="sales-draft-missing" />
      {!v.exists ? <Start v={v} onView={onView} /> : <Draft v={v} onView={onView} />}
    </>
  );
}

function Start({ v, onView }) {
  const [busy, setBusy] = useState(false);
  return (
    <div data-testid="sales-draft-start-box" className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-600 space-y-3">
      <p>No listing draft yet. Starting one fills it in from the record — a title and description from a template, no AI. The {v.kind} itself is never changed.</p>
      {v.can_start
        ? <button type="button" data-testid="sales-draft-start" disabled={busy} onClick={async () => { setBusy(true); await draftAct(v, "post", "/start", null, onView); setBusy(false); }}
          className="h-11 px-4 rounded-lg bg-[#1F1F1F] text-white font-bold disabled:opacity-40">Start the listing draft</button>
        : <p data-testid="sales-draft-cannot-start">Only someone with "Prepare listings" can start it, and only while it is in the sales queue.</p>}
    </div>
  );
}

function Draft({ v, onView }) {
  return (
    <>
      <DraftBlocks blocks={v.blocks} />
      <DraftFirewall v={v} onView={onView} />
      <section data-testid="sales-draft-fields" className="rounded-xl border border-slate-200 bg-white px-3">
        <div className="hidden xl:grid grid-cols-2 gap-x-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide">
          <div className="text-[#B01E28] border-b-2 border-red-200 pb-1">The record — private</div>
          <div className="text-emerald-800 border-b-2 border-emerald-200 pb-1">The listing — public</div>
        </div>
        {v.fields.map((r) => <DraftField key={`${r.key}-${r.rev}`} v={v} row={r} onView={onView} />)}
      </section>
      <DraftPhotos v={v} onView={onView} />
      <DraftReview v={v} onView={onView} />
      <DraftPreview l={v.listing} />
      <DraftSeo v={v} />
    </>
  );
}
